import React from "react";
import styles from "../VariantExplorer.module.css";

const formatLeadTime = (hours) => {
  if (typeof hours !== "number" || isNaN(hours) || hours < 0) return "–";

  const days = hours / 24;

  if (hours < 1) {
    return `${hours.toFixed(1)} h`;
  } else if (days < 1) {
    return `${hours.toFixed(1)} h`;
  } else if (days < 30) {
    return `${days.toFixed(2)} d`;
  } else {
    const months = days / 30;
    return `${months.toFixed(2)} mo`;
  }
};

const formatCount = (num) => {
  if (typeof num !== "number" || isNaN(num)) return "–";
  if (num < 1000) return num.toString();
  return (num / 1000).toFixed(2) + "K";
};

const getMedian = (values) => {
  if (!Array.isArray(values) || values.length === 0) return "–";
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

const VariantTable = ({
  variants,
  currentVariants,
  selectedVariants,
  handleToggleSelect,
  toggleSelectAll,
  sortKey,
  sortOrder,
  handleColumnSort,
  loading,
  others,
}) => {
  const renderSortArrow = (key) =>
    sortKey === key ? (sortOrder === "asc" ? "↑" : "↓") : "";

  const othersStepsArray = variants
    .filter((v) => !selectedVariants.includes(v.variant_id))
    .map((v) => v.num_steps);
  const othersMedianSteps = getMedian(othersStepsArray);

  return (
    <table className={styles["variant-table"]}>
      <thead>
        <tr>
          <th>
            <input
              type="checkbox"
              checked={selectedVariants.length === variants.length}
              onChange={toggleSelectAll}
              disabled={loading}
            />
          </th>
          <th
            onClick={() => handleColumnSort("variant_id")}
            className={styles["sortable"]}
          >
            Variant {renderSortArrow("variant_id")}
          </th>
          <th
            onClick={() => handleColumnSort("trace_count")}
            className={`${styles["sortable"]} ${styles["numeric"]}`}
          >
            Count {renderSortArrow("trace_count")}
          </th>
          <th
            onClick={() => handleColumnSort("percentage")}
            className={styles["sortable"]}
          >
            Cases Covered {renderSortArrow("percentage")}
          </th>
          <th
            onClick={() => handleColumnSort("num_steps")}
            className={`${styles["sortable"]} ${styles["numeric"]}`}
          >
            Steps {renderSortArrow("num_steps")}
          </th>
          <th
            onClick={() => handleColumnSort("median_lead_time")}
            className={`${styles["sortable"]} ${styles["numeric"]}`}
          >
            Median Lead Time {renderSortArrow("median_lead_time")}
          </th>
        </tr>
      </thead>
      <tbody>
        {currentVariants.map((v) => (
          <tr key={v.variant_id}>
            <td>
              <input
                type="checkbox"
                disabled={loading}
                checked={selectedVariants.includes(v.variant_id)}
                onChange={() => handleToggleSelect(v.variant_id)}
              />
            </td>
            <td>{v.variant_id}</td>
            <td className={styles["numeric"]}>{v.trace_count}</td>
            <td>
              <div className={styles["bar-wrapper"]}>
                <div className={styles["bar-container"]}>
                  <div
                    className={styles["bar"]}
                    style={{ width: `${v.percentage}%` }}
                  ></div>
                </div>
                <span className={styles["bar-label"]}>{v.percentage}%</span>
              </div>
            </td>
            <td className={styles["numeric"]}>{v.num_steps}</td>
            <td className={styles["numeric"]}>
              {formatLeadTime(v.lead_time_stats?.median)}
            </td>
          </tr>
        ))}
        <tr className={styles["others-row"]}>
          <td></td>
          <td>
            <strong>Others</strong>
          </td>
          <td className={styles["numeric"]}>
            {formatCount(others.object_count)}
          </td>
          <td>
            <div className={styles["bar-wrapper"]}>
              <div className={`${styles["bar-container"]} ${styles["others"]}`}>
                <div
                  className={styles["bar"]}
                  style={{ width: `${Math.max(others.percentage, 0.5)}%` }}
                  title={`Exact: ${others.percentage.toFixed(2)}%`}
                ></div>
              </div>
              <span className={styles["bar-label"]}>
                {others.percentage === 0
                  ? "0%"
                  : others.percentage < 1
                  ? "<1%"
                  : `${others.percentage.toFixed(2)}%`}
              </span>
            </div>
          </td>
          <td className={styles["numeric"]}>
            {formatCount(othersMedianSteps)}
          </td>
          <td className={styles["numeric"]}>
            {formatLeadTime(others.median_lead_time)}
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default VariantTable;
